import React, { useEffect, useState } from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import { applyActionCode, sendEmailVerification, checkActionCode } from 'firebase/auth';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { auth, db } from '../../firebase/firebase';
import { doc, updateDoc } from 'firebase/firestore';
import { Header, Icon, Button, Loader } from 'semantic-ui-react';
import { toast } from 'react-toastify';
import { useDispatch, useSelector } from 'react-redux';
import { setCurrentUser } from '../../actions';
import '../SignUp/auth-forms.css';

const VerifyEmail = () => {
  const location = useLocation();
  const history = useHistory();
  const dispatch = useDispatch();
  const authUser = useSelector(state => state.auth.currentUser);
  const [status, setStatus] = useState('verifying'); // verifying, success, error
  const [resending, setResending] = useState(false);

  useEffect(() => {
    const verify = async () => {
      const queryParams = new URLSearchParams(location.search);
      const oobCode = queryParams.get('oobCode');

      if (!oobCode) {
        setStatus('error');
        return;
      }

      try {
        // Get email from the code to update Firestore later
        const info = await checkActionCode(auth, oobCode);
        const email = info.data.email;
        console.log('[VerifyEmail] Extracted email from code:', email);

        await applyActionCode(auth, oobCode);
        console.log('[VerifyEmail] Action code applied successfully.');

        // Call backend to sync verified status to Firestore
        if (email) {
            try {
                const functions = getFunctions();
                const markEmailVerified = httpsCallable(functions, 'markEmailVerified');
                const result = await markEmailVerified({ email });
                console.log('[VerifyEmail] Backend sync result:', result.data);
            } catch (syncError) {
                console.error('[VerifyEmail] Backend sync failed (non-fatal):', syncError);
            }
        }

        // Force token refresh to update emailVerified status in the app
        if (auth.currentUser) {
            await auth.currentUser.reload();
            
            // Sync verified status to Firestore immediately
            try {
                await updateDoc(doc(db, 'users', auth.currentUser.uid), { emailVerified: true });
            } catch (dbError) {
                console.error("Error updating Firestore verification status:", dbError);
            }

            // Sync with Redux to ensure UI updates immediately
            if (authUser) {
                dispatch(setCurrentUser({ ...authUser, emailVerified: true }));
            }
        }
        setStatus('success');
      } catch (error) {
        console.error('Email verification error:', error);
        setStatus('error');
      }
    };

    verify();
  }, [location.search]);

  const handleResend = async () => {
    if (!auth.currentUser) {
      toast.info('Please log in to resend the verification email.');
      return;
    }
    setResending(true);
    try {
      await sendEmailVerification(auth.currentUser);
      toast.success('Verification email sent! Please check your inbox.');
    } catch (error) {
      console.error('Error resending verification email:', error);
      toast.error('Failed to send verification email.');
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="login-background">
      <div className="login-form-container">
        <div className="login-form" style={{ height: 'auto', minHeight: '500px', justifyContent: 'center', alignItems: 'center', padding: '40px' }}>
          {status === 'verifying' && (
            <div style={{ textAlign: 'center' }}>
              <Loader active inline="centered" size="large" />
              <Header as="h2" icon style={{ marginTop: '20px' }}>
                Verifying your email...
              </Header>
            </div>
          )}

          {status === 'success' && (
            <div style={{ textAlign: 'center' }}>
              <Icon name="check circle outline" color="green" size="huge" />
              <Header as="h2">Email Verified!</Header>
              <p style={{ fontSize: '1.2rem', marginTop: '1rem', color: '#555' }}>
                Thank you for verifying your email address. Your account is now active.
              </p>
              <div style={{ marginTop: '2rem', display: 'flex', flexDirection: 'column', gap: '10px', width: '100%' }}>
                  <Button primary size="large" onClick={() => history.push(auth.currentUser ? '/profile/edit' : '/login')} className="submit-button" style={{ width: '100%', margin: 0 }}>
                      {auth.currentUser ? 'Go to Profile' : 'Log In'}
                  </Button>
                  <Button basic size="large" onClick={() => history.push('/')} style={{ width: '100%', margin: 0 }}>
                      Go Home
                  </Button>
              </div>
            </div>
          )}

          {status === 'error' && (
            <div style={{ textAlign: 'center' }}>
              <Icon name="times circle outline" color="red" size="huge" />
              <Header as="h2">Verification Failed</Header>
              <p style={{ fontSize: '1.2rem', marginTop: '1rem', color: '#555' }}>
                The verification link is invalid or has expired. Please try requesting a new verification email from your profile.
              </p>
              <div style={{ marginTop: '2rem', display: 'flex', flexDirection: 'column', gap: '10px', width: '100%' }}>
                <Button primary onClick={() => history.push('/profile/edit')} className="submit-button" style={{ width: '100%', margin: 0 }}>
                  Go to Profile
                </Button>
                {auth.currentUser && (
                  <Button
                    color="teal"
                    onClick={handleResend}
                    loading={resending}
                    disabled={resending}
                    style={{ width: '100%', margin: 0 }}
                  >
                    Resend Verification Email
                  </Button>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default VerifyEmail;